import React, {
  useRef, createRef, useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import styled from '@emotion/styled';
import Router, { useRouter } from 'next/router';
import { connect } from 'react-redux';
import Link from 'next/link';

import PerfectScrollbarStyles from '../../theme/perfect-scrollbar';
import { getBasePath } from '../../utils/navigation';
import { logAmplitude } from '../../utils/amplitude';

import Checkbox, { StyledCheckbox } from '../../atoms/checkbox';
import inputSearch from '../../atoms/input-search';
import { getActiveMini } from '../filter-list-remove';
import { updateActiveMini } from '../../store/modules/petites/actions';

const FiltersWrapper = styled.div`
  overflow: hidden;
  max-height: ${(props) => (props.isOpened ? '18rem' : '0')};
  transition: ${(props) => props.theme.animation.default} max-height ${(props) => props.theme.animation.easeOutQuart};

  ${PerfectScrollbarStyles}
`;

const Filters = styled(PerfectScrollbar)`
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  max-height: 14rem;
  position: relative;
`;

const Search = styled(inputSearch)`
  outline: 0;
  background-color: transparent;
  color: ${(props) => props.theme.color.white};
  border: 1px solid ${(props) => props.theme.color.white};
  ${(props) => props.theme.search(props.theme.color.white, '12px center', 12)}

  ::placeholder {
    color: ${(props) => props.theme.color.white};
    opacity: 0.7;
  }
  ::-moz-placeholder {
    color: ${(props) => props.theme.color.white};
    opacity: 0.7;
  }
  ::-webkit-input-placeholder {
    color: ${(props) => props.theme.color.white};
    opacity: 0.7;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    color: ${(props) => props.theme.color.brand};
    border: 1px solid ${(props) => props.theme.color.brand};
    ${(props) => props.theme.search(props.theme.color.brand, '12px center', 12)}

    ::placeholder {
      color: ${(props) => props.theme.color.brand};
      opacity: 0.8;
    }
    ::-moz-placeholder {
      color: ${(props) => props.theme.color.brand};
      opacity: 0.8;
    }
    ::-webkit-input-placeholder {
      color: ${(props) => props.theme.color.brand};
      opacity: 0.8;
    }
  }
`;

const Filter = styled(Checkbox)`
  color: ${(props) => props.theme.color.white};

  &::before {
    border-color: ${(props) => props.theme.color.white};
  }

  &::after {
    background: ${(props) => props.theme.color.white};
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    color: ${(props) => props.theme.color.brand};

    &::before {
      border-color: ${(props) => props.theme.color.brand};
    }

    &::after {
      background: ${(props) => props.theme.color.brand};
    }
  }
`;

const FilterAnchor = styled(StyledCheckbox)`
  color: ${(props) => props.theme.color.white};
  text-decoration: none;

  &::before {
    border-color: ${(props) => props.theme.color.white};
  }

  &::after {
    background: ${(props) => props.theme.color.white};
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    color: ${(props) => props.theme.color.brand};

    &::before {
      border-color: ${(props) => props.theme.color.brand};
    }

    &::after {
      background: ${(props) => props.theme.color.brand};
    }
  }
`;

const FilterCount = styled.span`
  margin-left: .6rem;
`;

const FilterList = (props) => {
  const filterRef = useRef([...Array(props.filters.length)].map(() => createRef()));
  const router = useRouter();
  const psRef = useRef(0);

  const [mini, setMini] = useState(getActiveMini(props.mini, props.minis));

  useEffect(() => {
    if (mini?.id !== props.mini?.id) setMini(mini);
  }, [mini]);

  const handleOnChange = (event, filter) => {
    if (event) event.preventDefault();

    const {
      cats = null,
      af,
      trend,
      edit,
      lpc,
      brand,
      page,
      ...otherQueries
    } = Router.query;

    const query = { ...otherQueries };

    const base = getBasePath(props).split('?')[0];
    const as = cats ? `${base}/${cats.join('/')}` : base;

    let filters = af ? Router.query.af.split(' ') : [];

    if (filter.selected) filters = filters.filter((f) => f !== `${props.facet.id}:${filter.id}`);
    else filters.push(`${props.facet.id}:${filter.id}`);

    if (filters.length > 0) query.af = filters.join(' ');

    const tempQueryFilters = query.af ? query.af.split(' ').filter((f) => !['cat1', 'cat2', 'cat3'].includes(f.split(':')[0])) : [];

    if (tempQueryFilters.length > 0) query.af = query.af.split(' ').filter((f) => !['cat1', 'cat2', 'cat3'].includes(f.split(':')[0])).join(' ');
    else delete query.af;

    if (filter.selected && mini) {
      const taxons = mini.taxons.filter((x) => x.category === props.facet.id);
      if (taxons.length > 0 && taxons?.find((x) => x.value === filter.id)) {
        props.updateActiveMini(0);
        global.document.cookie = 'maisonette_active_mini=0; path=/';
      }
    }

    logAmplitude('Clicked Facet', {
      navigationItem: `${props.facet.id}:${filter.id}`,
      selected: !filter.selected,
      facets: filters?.filter((x) => !['cat1', 'cat2', 'cat3'].includes(x.split(':')[0]))?.join(';')
    });

    if (Router?.query?.page) delete Router?.query?.page;

    Router.push({
      pathname: Router.pathname,
      query: { ...Router.query, af: filters.join(' ') }
    }, {
      pathname: as,
      query
    }).then(() => {
      props.setOpened(false);
    });
  };

  const handleOnSearchInput = (e) => {
    const query = e.target.value.trim().toLowerCase();
    filterRef.current.forEach((f) => {
      const filter = f.current;
      const value = filter.props.value.trim().toLowerCase();
      const elem = global.document.getElementById(filter.props.id).parentElement;

      if (!query) {
        elem.classList.remove('is-hidden');
      } else if (value.indexOf(query) !== -1) {
        elem.classList.remove('is-hidden');
      } else {
        elem.classList.add('is-hidden');
      }
    });

    psRef.current.updateScroll();
  };

  useEffect(() => {
    if (psRef.current) psRef.current.updateScroll();
  }, [psRef]);

  const renderFilters = (filter, i) => {
    let { asPath = '' } = router ?? {};
    [asPath] = asPath.split('?');
    const [page, ...paths] = asPath.split('/').filter((x) => x);

    const getFilterHref = () => `${asPath}?af=${props.facet.id}:${filter.id}`;

    const checkboxFilters = () => (
      <Filter
        ref={filterRef.current[i]}
        forwardRef={filterRef.current[i]}
        active={filter.selected}
        changed={(e) => handleOnChange(e, filter)}
        id={`filter-${filter.id}`}
        key={`${filter.id}`}
        name={`filter-${filter.id}`}
        value={filter.name}
      >
        {filter.name}
        {filter.count > 0 && (
        <FilterCount>
          {`(${filter.count})`}
        </FilterCount>
        )}
      </Filter>
    );

    const hyperlinkFilters = (allowlist = props.facet?.values?.map?.((x) => x.id)) =>
      (allowlist.includes(filter.id) ? (
        <Link key={`${filter.id}`} href={getFilterHref(filter)} passHref>
          <FilterAnchor
            as="a"
            ref={filterRef.current[i]}
            forwardRef={filterRef.current[i]}
            active={filter.selected}
            onClick={(e) => handleOnChange(e, filter)}
            id={`filter-${filter.id}`}
            name={`filter-${filter.id}`}
            value={filter.name}
          >
            {filter.name}
            {filter.count > 0 && (
            <FilterCount>
              {`(${filter.count})`}
            </FilterCount>
            )}
          </FilterAnchor>
        </Link>
      ) : checkboxFilters());

    if (page === 'shop') {
      const [cat1, cat2, cat3] = paths;

      if (cat1 === 'baby' && cat2) {
        if (cat3 && ['agerange', 'color'].includes(props.facet.id)) return hyperlinkFilters();

        if (props.facet.id === 'category') {
          if (cat2 === 'boy-clothing') {
            return hyperlinkFilters([
              'onesies',
              'leggings',
              'sweaters',
              'overalls',
              'rashguards',
              'sweaterscardigans',
              'vests',
              'snowsuits'
            ]);
          }

          if (cat2 === 'girl-clothing') {
            return hyperlinkFilters([
              'onesies',
              'leggings',
              'overalls',
              'sweaterscardigans',
              'rashguards',
              'coverups',
              'poloshirts',
              'vests',
              'snowsuits'
            ]);
          }

          if (cat2 === 'girl-accessories') {
            return hyperlinkFilters([
              'slipons',
              'maryjanes',
              'sneakers',
              'boots',
              'cribshoes',
              'dressshoes'
            ]);
          }

          if (cat2 === 'boy-accessories') {
            return hyperlinkFilters([
              'sneakers',
              'slipons',
              'boots',
              'bowtiesties',
              'cribshoes'
            ]);
          }

          if (cat2 === 'bath-body') {
            return hyperlinkFilters([
              'bodymoisturizers',
              'robes',
              'bodycleansers',
              'supplements'
            ]);
          }
        }
      } else if (cat1 === 'kids' && cat2) {
        if (cat3 && ['color'].includes(props.facet.id)) return hyperlinkFilters();

        if (props.facet.id === 'category') {
          if (cat2 === 'girl-clothing') {
            return hyperlinkFilters([
              'leggings',
              'rashguards',
              'playdresses',
              'sweatpants',
              'coverups',
              'sweaterscardigans',
              'overalls',
              'jeans',
              'puffersdownjackets',
              'woolcoats',
              'parkas',
              'vests',
              'underwear',
              'poloshirts',
              'snowsuits',
              'snowpants'
            ]);
          }

          if (cat2 === 'girl-accessories') {
            return hyperlinkFilters([
              'maryjanes',
              'sneakers',
              'sandals',
              'flats',
              'boots',
              'earrings',
              'dressshoes',
              'lunchbags',
              'loafers',
              'rainboots',
              'umbrellas',
              'charms'
            ]);
          }

          if (cat2 === 'boy-clothing') {
            return hyperlinkFilters([
              'sweatpants',
              'rashguards',
              'poloshirts',
              'jeans',
              'buttondowns',
              'sweaterscardigans',
              'Overalls',
              'puffersdownjackets',
              'vests',
              'parkas',
              'blazers',
              'snowsuits',
              'suitsseparates',
              'coats',
              'snowpants'
            ]);
          }

          if (cat2 === 'boy-accessories') {
            return hyperlinkFilters([
              'sneakers',
              'boots',
              'loafers',
              'lunchbags',
              'oxfords',
              'rainboots',
              'skigoggles'
            ]);
          }

          if (cat2 === 'bath-body') {
            return hyperlinkFilters([
              'robes',
              'bathsaltssoaks',
              'shampoos',
              'conditioners'
            ]);
          }
        }
      } else if (cat1 === 'home' && cat2) {
        if (cat3 && ['gender'].includes(props.facet.id)) return hyperlinkFilters(['boy', 'babyboy', 'girl', 'babygirl', 'men', 'women']);

        if (props.facet.id === 'category') {
          if (cat2 === 'decor') {
            return hyperlinkFilters([
              'wallpaper',
              'decorations',
              'mobiles',
              'drinkware'
            ]);
          }

          if (cat2 === 'furniture') {
            return hyperlinkFilters([
              'teepees',
              'travelcribs'
            ]);
          }

          if (cat2 === 'bedding-bath') {
            return hyperlinkFilters([
              'cribsheets',
              'tubs',
              'cribskirts'
            ]);
          }
        }
      } else if (cat1 === 'play' && cat2 && !cat3) {
        if (props.facet.id === 'category') {
          if (cat2 === 'kids') {
            return hyperlinkFilters([
              'costumes',
              'makeup',
              'woodens'
            ]);
          }

          if (cat2 === 'outdoor') {
            return hyperlinkFilters([
              'goggles',
              'helmets',
              'poolfloats'
            ]);
          }
        }
      } else if (cat1 === 'gear' && !cat2 && !cat3) {
        if (props.facet.id === 'category') {
          return hyperlinkFilters([
            'strolleraccessories',
            'foodstorage',
            'sippycups',
            'headphones',
            'bottles',
            'babymonitors',
            'pacifiers',
            'burpcloths',
            'doublestrollers',
            'boosterseats',
            'nursingpillows'
          ]);
        }
      }
    }

    if (page === 'brands') {
      const [, cat1] = paths;
      if (!cat1 && ['category'].includes(props.facet.id)) return hyperlinkFilters();
    }

    return (
      <Filter
        ref={filterRef.current[i]}
        forwardRef={filterRef.current[i]}
        active={filter.selected}
        changed={(e) => handleOnChange(e, filter)}
        id={`filter-${filter.id}`}
        key={`${filter.id}`}
        name={`filter-${filter.id}`}
        value={filter.name}
      >
        {filter.name}
        {filter.count > 0 && (
          <FilterCount>
            {`(${filter.count})`}
          </FilterCount>
        )}
      </Filter>
    );
  };

  return (
    <FiltersWrapper isOpened={props.isOpened}>
      {
        props.facet.id === 'brand' && (
          <Search placeholder={`Search ${props.facet.name}`} onInput={handleOnSearchInput} />
        )
      }

      <Filters ref={psRef} className="ps">
        { props.filters.map((filter, i) => renderFilters(filter, i)) }
      </Filters>
    </FiltersWrapper>
  );
};

FilterList.defaultProps = {
  mini: 0,
  minis: []
};

FilterList.propTypes = {
  mini: PropTypes.number,
  minis: PropTypes.array,
  filters: PropTypes.array.isRequired,
  facet: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  setOpened: PropTypes.func.isRequired,
  updateActiveMini: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  mini: state.petites.active_mini,
  minis: state.petites.minis
});

const mapDispatchToProps = (dispatch) => ({
  updateActiveMini: (id) => dispatch(updateActiveMini(id))

});

const ConnectedFilterList = connect(mapStateToProps, mapDispatchToProps)(FilterList);

FilterList.displayName = 'FilterList';

export default ConnectedFilterList;
